import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { mediaSrc } from "../../../utils/mediaSrc";
import { fetchInfModels } from "../../influencers/infModelsApi";

function absoluteMediaUrl(stored) {
  const display = mediaSrc(stored) || String(stored || "").trim();
  if (!display) return "";
  if (/^https?:\/\//i.test(display)) return display;
  if (/^https?:\/\//i.test(String(stored || ""))) return String(stored).trim();
  if (typeof window === "undefined") return display;
  const path = display.startsWith("/") ? display : `/${display}`;
  return `${window.location.origin}${path}`;
}

function ImagePickTile({ item, selected, disabled, onToggle }) {
  const src = mediaSrc(item.photo_url) || item.photo_url;
  return (
    <Box
      onClick={() => !disabled && onToggle(item.id)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !selected ? 0.45 : 1,
        bgcolor: alpha("#000", 0.04),
        "&:hover": disabled ? {} : { borderColor: selected ? "primary.main" : "primary.light" },
      }}
    >
      <Checkbox
        checked={selected}
        disabled={disabled && !selected}
        onChange={() => onToggle(item.id)}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          bgcolor: alpha("#fff", 0.92),
          borderRadius: 1,
          p: 0.25,
        }}
      />
      <Box sx={{ pt: "100%", position: "relative" }}>
        {src ? (
          <Box
            component="img"
            src={src}
            alt=""
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageOutlined color="disabled" />
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1, bgcolor: "background.paper" }}>
        <Typography variant="caption" fontWeight={700} display="block" noWrap>
          {item.name || `Image #${item.id}`}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Pick completed Image Studio images to use as video references.
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   maxSelect?: number;
 *   onConfirm: (items: Array<{ id: string|number; previewUrl: string; name: string }>) => void;
 *   lang?: object;
 * }} props
 */
export default function PickImageStudioModal({ open, onClose, maxSelect = 1, onConfirm, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [images, setImages] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchInfModels(hitAxios);
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setImages(
          res.data.data.filter(
            (row) => row?.status === "active" && (row.photo_url || row.photoUrl),
          ),
        );
      } else {
        setImages([]);
      }
    } catch (err) {
      showSnack?.(err?.message || lang?.videoStudioImageLibraryLoadFailed || "Could not load images.", false);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, lang, showSnack]);

  React.useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelectedIds(new Set());
    load();
  }, [open, load]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter((img) => String(img.name || "").toLowerCase().includes(q));
  }, [images, search]);

  const slotsLeft = Math.max(0, maxSelect - selectedIds.size);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= maxSelect) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const picked = [];
    for (const img of images) {
      if (!selectedIds.has(img.id)) continue;
      const raw = img.photo_url || img.photoUrl;
      const previewUrl = absoluteMediaUrl(raw);
      if (!previewUrl) continue;
      picked.push({
        id: img.id,
        previewUrl,
        name: img.name || `Image #${img.id}`,
      });
    }
    if (picked.length) onConfirm(picked);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <ImageOutlined color="primary" />
        <Box sx={{ flex: 1 }}>{lang?.videoStudioImageLibraryTitle || "Choose from Image Studio"}</Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {lang?.videoStudioImageLibraryHint ||
              "Select images you generated in Image Studio to use as video references."}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={lang?.search || "Search"}
            placeholder={lang?.videoStudioImageLibrarySearchPh || "Search by name…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {maxSelect > 1 ? (
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {(lang?.videoStudioLibrarySlotsLeft || "{n} slot(s) remaining").replace(
                "{n}",
                String(Math.max(0, maxSelect - selectedIds.size)),
              )}
            </Typography>
          ) : null}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              {lang?.videoStudioImageLibraryEmpty ||
                "No Image Studio images yet. Generate images first, then return here."}
            </Typography>
          ) : (
            <Grid container spacing={1.5}>
              {filtered.map((item) => (
                <Grid item xs={6} sm={4} md={3} key={item.id}>
                  <ImagePickTile
                    item={item}
                    selected={selectedIds.has(item.id)}
                    disabled={slotsLeft === 0 && !selectedIds.has(item.id)}
                    onToggle={toggle}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={selectedIds.size === 0}
          onClick={handleConfirm}
          sx={{ fontWeight: 800 }}
        >
          {(lang?.videoStudioUseSelected || "Use selected") + ` (${selectedIds.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
